import React, { useEffect, useState } from 'react';
import { CartContextProvider } from '../../context/CartContext/CartContext';
import ReviewPortfolioAllocation from '../../components/ReviewPortfolioSection/ReviewPortfolioSection';
import ReviewPersonalInformation from '../../components/ReviewPersonalInformation/ReviewPersonalInformation';
import ReviewContactInformation from '../../components/ReviewContactInformation/ReviewContactInformation';
import ReviewCombinedPersonalInformation from '../../components/ReviewCombinedPersonalInformation/ReviewCombinedPersonalInformation';
import {
  formatFunds,
  accountTypes,
  consumerTypes,
  isRetirementIncomeFundPresent,
  isContributory,
  isTaxable,
  isTaxableTransfer,
  isTrustExistingFlow,
  isNonFundedFlow
} from '../../flows/shared/utils';
import { REVIEW_COMMIT_MESSAGE } from '../../core/flow-engine';
import TermsConditionsRenderer from '../../components/TermsConditionsRenderer/TermsConditionsRenderer';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import styles from './style.css';
import ReviewEditFunding from '../../components/ReviewEditFunding/ReviewEditFunding';
import Heading from '../../components/Typography/Heading/Heading';
import SubHeading from '../../components/Typography/SubHeading/SubHeading';
import SectionHeading from '../../components/Typography/SectionHeading/SectionHeading';
import EditButton from '../../components/Typography/EditButton/EditButton';
import {
  getHeaderText,
  hasMinorInformation,
  showAdditionalCustomers,
  showBenes
} from './utils';
import { getRewardsAvailable } from '../../flows/shared/summit.utils';
import ReviewBeneficiaries from '../../components/ReviewBeneficiaries/ReviewBeneficiaries';
import ReviewAdditionalCustomers from '../../components/ReviewAdditionalCustomers/ReviewAdditionalCustomers';
import ReviewTrustInformation from '../../components/ReviewTrustInformation/ReviewTrustInformation';
import ReviewTrustAllocation from '../../components/ReviewTrustAllocation/ReviewTrustAllocation';
import ApplyBenesModal from '../../components/ApplyBenesModal/ApplyBenesModal';
import { isLouisianaResident } from '../../flows/shared/testers';
/* eslint-disable complexity */

const ReviewScreen = ({ appState, next, flowProps }) => {
  const {
    store: {
      accountType,
      chargeType,
      consumerName,
      enabled,
      existingAgreements = [],
      app: {
        customer,
        investmentMethod,
        beneficiaries,
        coTrustees,
        jointOwners = [],
        trust,
        trustee,
        fundingMethodType,
        iraAccountType
      },
      additionalCustomers,
      amount,
      termsData,
      chosenInvestments,
      summitData: { assetTierID = 0 } = {}
    }
  } = appState;

  const [displayApplyBenesModal, setDisplayApplyBenesModal] = useState(false);
  useEffect(() => {
    const isRolloverorTransfer = [
      accountTypes.TRANSFER,
      accountTypes.ROLLOVER
    ].includes(accountType);
    if (existingAgreements.length && isRolloverorTransfer) {
      setDisplayApplyBenesModal(true);
    }
  }, []);

  const { fundLaterFlow = false } = flowProps;

  const formattedSelectedFunds = formatFunds(chosenInvestments);
  const hasRetirementIncomeFund = isRetirementIncomeFundPresent(
    chosenInvestments
  );

  const rewardsAvailable = getRewardsAvailable(
    accountType,
    trust,
    trustee,
    assetTierID
  );

  const showFundingSection = () =>
    !fundLaterFlow &&
    !isNonFundedFlow(accountType) &&
    !isTrustExistingFlow(accountType, fundingMethodType) &&
    !isTaxableTransfer(accountType, chargeType);

  const allowEditFunding = () => {
    if (consumerName === consumerTypes.FONASU) return true;
    if (!isTaxable(accountType) && !isContributory(accountType)) return true;

    return false;
  };

  // provide commit message to show instead of createApplication's
  // utilizes next function `result` arg
  const handleClick = chosen => next(chosen, REVIEW_COMMIT_MESSAGE);

  const getSubheaderText = () => {
    if (fundLaterFlow)
      return 'Please review and submit your information. You can buy shares of the investments you selected after opening your account.';
    return 'Please review, confirm, and submit your information.';
  };

  return (
    <div className="wrapper">
      <Heading text="Almost there..." />
      <SubHeading text={getSubheaderText()} />
      {isTrustExistingFlow(accountType, fundingMethodType) ? (
        <div className="review-section">
          <SectionHeading text={getHeaderText(accountType, iraAccountType)}>
            <EditButton
              onClick={handleClick('existing-account')}
              currentlyEditing={false}
            />
          </SectionHeading>
          <div>
            <ReviewTrustAllocation
              totalAmount={amount}
              selectedFunds={chosenInvestments}
            />
          </div>
        </div>
      ) : (
        <div className="review-section">
          <SectionHeading text={getHeaderText(accountType, iraAccountType)}>
            <EditButton onClick={handleClick(investmentMethod.toLowerCase())} />
          </SectionHeading>

          <div>
            <CartContextProvider
              initialTotalAmount={amount}
              initialPercentMode
              initialFunds={formattedSelectedFunds}>
              <ReviewPortfolioAllocation
                formattedSelectedFunds={formattedSelectedFunds}
                rewardsAvailable={rewardsAvailable}
                small
                ugmaFundMinimum={flowProps?.fundMinimum}
                isTaxable={isTaxable(accountType)}
                handleClick={handleClick('byop')}
                fundLaterFlow={fundLaterFlow}
                amount={amount}
              />
            </CartContextProvider>
          </div>
        </div>
      )}
      <div className="blocked_data">
        {showFundingSection() && (
          <div className="review-section">
            <ReviewEditFunding
              shouldAllowEditing={allowEditFunding()}
              handleClick={handleClick}
            />
          </div>
        )}

        <div className="review-section">
          {accountType === accountTypes.TRUST && (
            <ReviewTrustInformation
              trust={trust}
              trustee={trustee}
              handleClick={handleClick}
              coTrustees={coTrustees}
            />
          )}

          {accountType !== accountTypes.TRUST &&
            flowProps?.displayCombinedInformation && (
              <ReviewCombinedPersonalInformation
                customer={customer}
                handleClick={handleClick}
              />
            )}
          {accountType !== accountTypes.TRUST &&
            !flowProps?.displayCombinedInformation && (
              <>
                <ReviewPersonalInformation
                  customer={customer}
                  disabled={enabled}
                  handleClick={handleClick}
                />
                <ReviewContactInformation
                  customer={customer}
                  handleClick={handleClick}
                />
              </>
            )}
        </div>
        {showAdditionalCustomers(additionalCustomers, accountType) && (
          <div className="review-section">
            <ReviewAdditionalCustomers
              additionalCustomers={additionalCustomers}
              handleClick={handleClick}
              hasMinors={
                hasMinorInformation(additionalCustomers) &&
                accountType === accountTypes.UGMAUTMA
              }
              accountType={accountType}
            />
          </div>
        )}
        {!isLouisianaResident(customer, jointOwners) &&
          showBenes(beneficiaries) && (
            <div className="review-section">
              <ReviewBeneficiaries
                beneficiaries={beneficiaries}
                handleClick={handleClick}
              />
            </div>
          )}
      </div>
      <div className="review-section">
        <TermsConditionsRenderer
          accountType={accountType}
          termsData={termsData}
          hasRetirementIncomeFund={Boolean(hasRetirementIncomeFund)}
        />
      </div>
      <PhoneNumber />
      {displayApplyBenesModal && (
        <ApplyBenesModal
          setDisplayApplyBenesModal={setDisplayApplyBenesModal}
        />
      )}
      <style jsx>{styles}</style>
    </div>
  );
};

export default ReviewScreen;

/* eslint-enable complexity */
